import * as React from 'react';
import { STATE } from 'app/constants';
import * as navigate from 'app/area/shared/navigation';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { SpinLoader, OverlayComponent, withPrompt, PromptProps } from 'app/components';
import { withOptions, OptionsProps } from 'app/components/options-handler';
import { Paper, PromptConfirmation } from 'ui';
import { CodetableSelectorProps } from 'app/area/indicator-factors/types';
import { actionCreators } from './actions';
import { CodetableAdministrationState } from '../types-store';
import { indicatorFactorsAdministrationTranslation } from '../constants';
import { selectors as commonSelectors, getCodetableMetadata } from '../selectors';
import { selectors as codeItemSelectors } from 'app/area/shared/codetable/item/selectors';
import { getCodetableId } from 'app/area/shared/codetable/breadcrumbs';
import {
  codeAccessConfiguration, handleCreateCodeItem, handleLoadCodeItem,
  readOnlyConfiguration, handleLoadCodetableOptions
} from 'app/area/indicator-factors/item/codetable-item-helpers';
import { validate } from 'app/area/indicator-factors/item/validation/validate';
import { routeConstants } from 'app/routes/route-constants';
import { IndicatorFactorsScreenRenderer } from 'app/area/indicator-factors/item/code-screen-renderer';
import { WithCodetableAccess } from 'app/components/metro-line/types';
import { IndicatorFactorsStickyHeader } from 'app/area/indicator-factors/item/sticky-header/indicator-factors-sticky-header';
import { scrollToTop } from 'app/infrastructure/platform/routing';
import { escapeUrl } from 'app/components/favourite-layout/favourite/common/utils';

export type IndicatorFactorsItemComponentProps = DefaultContainerProps & OptionsProps & PromptProps &
  CodetableAdministrationState & CodetableSelectorProps & typeof actionCreators;

type CodetableItemComponentState = WithCodetableAccess;

export class IndicatorFactorsItemComponent extends React.PureComponent<IndicatorFactorsItemComponentProps, CodetableItemComponentState> {
  public static statePath: string = STATE.indicatorFactors;

  public static getDerivedStateFromProps(props: IndicatorFactorsItemComponentProps, state: CodetableItemComponentState) {
    const codes = props?.access?.data?.codes;
    return { codes: (codes !== state.codes) ? codes : undefined };
  }

  constructor(props: IndicatorFactorsItemComponentProps) {
    super(props);

    this.state = {
      codes: props?.access?.data?.codes
    };
  }

  public componentDidMount() {
    scrollToTop();
    !this.state.codes && this.props.clearCodesAccess();
    handleCreateCodeItem(this.getPropsWithCodetableId(this.props));
  }

  public componentDidUpdate(prevProps: IndicatorFactorsItemComponentProps) {
    const propsWithCodetableId = this.getPropsWithCodetableId(this.props);
    const prevPropsWithCodetableId = this.getPropsWithCodetableId(prevProps);

    handleLoadCodeItem(propsWithCodetableId, prevPropsWithCodetableId);
    handleLoadCodetableOptions(propsWithCodetableId, prevPropsWithCodetableId);
  }

  public componentWillUnmount() {
    this.resetForm();
  }

  private getPropsWithCodetableId = (props: IndicatorFactorsItemComponentProps) => (props
    ? { ...props, codetableId: getCodetableId(props.breadcrumbs), actionType: props.params && props.params.actionType }
    : undefined
  );

  private save = () => {
    const { saveCodetableItem, updateValidation, metadata: { data: metadata }, breadcrumbs,
      options, item: { codeItem },
      translation, params: { actionType } } = this.props;

    const codetableId = getCodetableId(breadcrumbs);

    updateValidation(validate({ codeItem, translation }));

    saveCodetableItem({
      codetableId,
      breadcrumbs,
      actionType,
      metadata, options, validationErrorMessage: translation.validation_error
    });
  };

  private cancel = () => navigate.replace(routeConstants.indicatorFactorsAdministrationFor(this.props.breadcrumbs));

  private resetForm = () => {
    const { resetCodeItem, clearValidation, clearCodesAccess } = this.props;

    clearCodesAccess();
    clearValidation();
    resetCodeItem();
  };

  private deleteCodeItem = () => {
    const { deleteCodeItem, translation, codeId, breadcrumbs } = this.props;

    deleteCodeItem({ breadcrumbs, codeId, successMessage: translation.code_item_successfully_deleted });
  };

  private duplicateCodeItem = () => {
    const { codeId, breadcrumbs } = this.props;
    navigate.replace(escapeUrl(routeConstants.indicatorFactorsAdministrationDuplicateCodeFor(breadcrumbs, codeId)));
    return;
  };

  public render() {

    const {
      item: {
        fetchingCodeId, fetchingCreateOptions, codeItem, savingCodetableItem, fetchingDelete } = {},
      item: { codeItem: { id: codeId } = {} } = {},
      metadata: { data } = {},
      access: { fetchingCodes, fetchingCodetables, data: { codetables: { create: canCreate, administrationCentral } = {},
        codes: { edit: canEdit, move: canMove } = {} } = {} } = {},
      breadcrumbs, translation, params: { actionType } = {}, shouldPrompt
    } = this.props;

    if (fetchingCodetables !== undefined || fetchingCodes !== undefined || fetchingCodeId !== undefined || fetchingCreateOptions) {
      return <SpinLoader />;
    }

    const codetableId = getCodetableId(breadcrumbs);

    const codetableMetadata = getCodetableMetadata(data, codetableId);

    const overlayText = savingCodetableItem ? translation.saving_code : translation.deleting_code;

    if (!codetableMetadata) {
      return null;
    }

    const readOnlyConfig = readOnlyConfiguration({
      actionType, codeId, administrationCentral, canCreate, canMove, canEdit, codetableMetadata
    });

    const access = codeAccessConfiguration(codetableId, this.props.access.data, undefined);

    return <div className="entity-view">
      <Paper componentId="code-base-paper" className={undefined}>
        <OverlayComponent
          displayOverlay={savingCodetableItem || fetchingDelete}
          overlayText={overlayText} />

        {this.props.shouldDisplayModal
          ? <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel}
          />
          : null}

        <IndicatorFactorsStickyHeader
          save={this.save}
          cancel={this.cancel}
          codeItem={codeItem}
          deleteCodeItem={access.canDelete && this.deleteCodeItem}
          duplicateCodeItem={access.canDuplicate && this.duplicateCodeItem}
          codetableName={codetableMetadata.label}
          codetableId={codetableId}
          codeId={this.props.codeId}
          translation={translation}
          readOnlyConfig={readOnlyConfig}
          breadcrumbs={breadcrumbs}
          actionType={actionType}
          metadata={codetableMetadata}
          shouldPrompt={shouldPrompt}
        />

        <IndicatorFactorsScreenRenderer
          {...this.props}
          {...this.props.item}
          {...this.props.sharedState}
          codetableId={codetableId}
          readOnlyConfig={readOnlyConfig}
          metadata={codetableMetadata}
          actionType={actionType}
        />
      </Paper>
    </div>;
  }
}

const selectors = { ...commonSelectors, ...codeItemSelectors };

const container = containerFactory(
  withOptions(withPrompt(IndicatorFactorsItemComponent) as any),
  actionCreators,
  selectors,
  indicatorFactorsAdministrationTranslation
);

export { container as IndicatorFactorsItem };
